import {
  Card,
  ResourceList,
  ResourceItem,
  Text,
  InlineStack,
  BlockStack,
  Box,
  Link,
  Icon,
  Divider,
  SkeletonBodyText,
  SkeletonDisplayText,
  InlineGrid,
} from "@medentic/polaris-react";
import { ExternalIcon, CalendarIcon, LocationIcon, ViewIcon, TeamIcon, LinkIcon } from "@medentic/polaris-icons";
import { formatStartDate } from "@shared/lib";
import { useTranslation } from "react-i18next";
import { useVenues } from "@features/events/hooks/useVenues";
import { useEvents } from "@features/events/hooks/useEventsDashboard";
import { EventStatusType, Event } from "@features/events/types";
import { RenderDate } from "@shared/ui";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import IconWithValueAndTooltip from "@features/events/components/common/IconWithValueAndTooltip";
import { getLocalizedText } from "@shared/lib";

export default function UpcomingEventsCard({
  hcc,
}: {
  hcc: string | undefined;
}) {
  const { t } = useTranslation();
  const skeletonRowIds = [1, 2, 3, 4, 5];
  const skeletonMetricIds = [1, 2, 3, 4, 5, 6];
  const { data: events = [], isLoading: loading } = useEvents(
    hcc, [EventStatusType.UPCOMING], "starting asc", 5
  ) as {
    data: Event[];
    isLoading: boolean;
  };
  const { data: venues = [], isLoading: venuesLoading } = useVenues({ hcc });

  if (loading) {
    return (
      <Card padding="0">
        {skeletonRowIds.map((rowId) => (
          <Box key={rowId}>
            {rowId > 1 && <Divider />}
            <Box padding="300">
              <InlineGrid
                columns={{ sm: 1, md: "1fr auto auto auto auto auto auto", lg: "1fr auto auto auto auto auto auto" }}
                gap={{ sm: "200", md: "300", lg: "300" }}
              >
                <BlockStack gap="200">
                  <Box maxWidth="420px">
                    <SkeletonDisplayText size="small" />
                  </Box>
                  <InlineStack gap="300" blockAlign="center" wrap={false}>
                    <Box minWidth="140px">
                      <SkeletonBodyText lines={1} />
                    </Box>
                    <Box minWidth="320px">
                      <SkeletonBodyText lines={1} />
                    </Box>
                  </InlineStack>
                </BlockStack>

                {skeletonMetricIds.map((metricId) => (
                  <InlineStack key={`${rowId}-${metricId}`} align="end" blockAlign="center">
                    <Box minWidth="72px">
                      <SkeletonDisplayText size="small" />
                    </Box>
                  </InlineStack>
                ))}
              </InlineGrid>
            </Box>
          </Box>
        ))}
      </Card>
    );
  }

  return (
    <Card padding="0">
      <ResourceList
        resourceName={{
          singular: t("eventDashboard.event"),
          plural: t("eventDashboard.events"),
        }}
        items={events}
        renderItem={(event: Event) => {
          const venueId = event.venueIds?.[0];
          const venue = venues.find((v) => v.id === venueId);

          return (
            <ResourceItem
              key={event.id}
              id={event.id}
              url={`/e/${hcc}/events/${event.id}`}
              accessibilityLabel={`View details for ${event.publicTitle}`}
            >
              <InlineGrid
                columns={{ sm: 1, md: 2, lg: "1fr auto auto auto auto auto auto" }}
                gap={{ sm: "200", md: "400", lg: "300" }}
              >
                <InlineStack align="start" blockAlign="center">
                  <BlockStack gap="200">
                    <Text as="span" variant="bodyMd" fontWeight="bold" truncate>
                      {event.publicTitle}
                    </Text>
                    <InlineStack gap="100" align="start" blockAlign="center" wrap={false}>
                      <Box>
                        <Icon source={CalendarIcon} tone="subdued" />
                      </Box>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {event.dateStart === event.dateEnd && event.dateStart ? (
                          <RenderDate dayFirst={true} date={event.dateStart} />
                        ) : (
                          <>
                            {formatStartDate(event.dateStart)}
                            {event.dateEnd && (
                              <>
                                {" "}
                                -{" "}
                                <RenderDate
                                  dayFirst={true}
                                  date={event.dateEnd}
                                />
                              </>
                            )}
                          </>
                        )}
                      </Text>
                      <Box>
                        <Icon source={LocationIcon} tone="subdued" />
                      </Box>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {venuesLoading ? (
                          <SkeletonBodyText lines={1} />
                        ) : (
                          <>
                            {getLocalizedText(venue?.geoAddress?.summary)}
                          </>
                        )}
                      </Text>
                    </InlineStack>
                  </BlockStack>
                </InlineStack>

                {/* Event badge time */}
                <InlineStack align="end" blockAlign="center">
                  <Box>
                    <EventBadgeRenderer
                      slug={event.status ?? ""}
                      entity="event"
                    />
                  </Box>
                </InlineStack>

                {/* Event badge time */}
                <InlineStack align="end" blockAlign="center">
                  <Box>
                    <EventBadgeRenderer
                      slug={event.visibility ?? 0}
                      entity="visibility"
                    />
                  </Box>
                </InlineStack>

                {/* Registered icon */}
                <InlineStack align="end" blockAlign="center">
                  <Box>
                    <IconWithValueAndTooltip
                      iconSource={TeamIcon}
                      value={event.spotsCurrent ?? 0}
                      tooltip={t("eventDashboard.registered")}
                      iconTone="subdued"
                      textTone="subdued"
                      alignment="end"
                    />
                  </Box>
                </InlineStack>

                {/* Views icon */}
                <InlineStack align="end" blockAlign="center">
                  <Box>
                    <IconWithValueAndTooltip
                      iconSource={ViewIcon}
                      value={event.views ?? 0}
                      tooltip={t("eventDashboard.views")}
                      iconTone="subdued"
                      textTone="subdued"
                      alignment="end"
                    />
                  </Box>
                </InlineStack>

                {/* Redirect clicks icon */}
                <InlineStack align="end" blockAlign="center">
                  <Box>
                    <IconWithValueAndTooltip
                      iconSource={LinkIcon}
                      value={event.bookRedirectLinkClicks ?? 0}
                      tooltip={t("eventDashboard.redirectClicks")}
                      iconTone="subdued"
                      textTone="subdued"
                      alignment="end"
                    />
                  </Box>
                </InlineStack>
                
                {/* External link icon */}
                <InlineStack align="end" blockAlign="center">
                  <Box>
                    <Link url={event.publicURL} removeUnderline>
                      <Icon source={ExternalIcon} tone="base" />
                    </Link>
                  </Box>
                </InlineStack>
              </InlineGrid>
            </ResourceItem>
          );
        }}
        emptyState={
          <Box padding="400">
            <Text as="span" variant="bodySm" tone="subdued">
              {t("eventDashboard.noEvents")}
            </Text>
          </Box>
        }
      />
    </Card>
  );
}
